import { create } from "zustand";
import type { AgentFocus } from "@/types/agentFocus.type";
import type { SlideTextSource } from "./components/slideTextSource";

export interface StrategySlideContext {
  brandId: string;
  strategyId: string;
  section: string;
  label: string;
  pillarId?: string;
}

/**
 * What the agent's composer carries from the deck, as an editor's chat does:
 * the open slide (until the person clears it to ask about the whole strategy)
 * and the words they highlighted on it. Client-only; never add presentation
 * fields to the API focus shape.
 */
export const useStrategySlideContext = create<{
  current: StrategySlideContext | null;
  /** Words highlighted on that slide. */
  selection: string | null;
  /** Whether those words are the deck's fixed labels, the strategy's text, or both. */
  selectionSource: SlideTextSource | null;
  /** The open slide's fixed labels: the deck's own copy, which nobody can edit. */
  labels: string[];
  /** The person cleared the open slide (X): nothing about it rides with a message until they highlight on it or open another. */
  cleared: boolean;
  /** A slide is open: it becomes the context, and any old highlight goes. */
  set: (current: StrategySlideContext | null) => void;
  /** Words highlighted on the open slide; a highlight brings a cleared slide back. */
  setSelection: (selection: string | null, source?: SlideTextSource | null) => void;
  setLabels: (labels: string[]) => void;
  /** Ask about the whole strategy instead of this slide. */
  dismiss: () => void;
}>()((set, get) => ({
  current: null,
  selection: null,
  selectionSource: null,
  labels: [],
  cleared: false,
  set: (current) => {
    const { current: was } = get();
    // A refetch re-announces the same slide; it must not undo the person's X.
    const same = !!was && !!current && `${was.strategyId}:${was.label}` === `${current.strategyId}:${current.label}`;
    set(same ? { current } : { current, selection: null, selectionSource: null, labels: [], cleared: false });
  },
  setSelection: (selection, source = null) => {
    if (!selection) releaseSlideHighlight();
    set(selection ? { selection, selectionSource: source, cleared: false } : { selection: null, selectionSource: null });
  },
  setLabels: (labels) => {
    if (labels.join("\n") !== get().labels.join("\n")) set({ labels });
  },
  dismiss: () => {
    releaseSlideHighlight();
    set({ selection: null, selectionSource: null, cleared: true });
  },
}));

/**
 * Let go of the words still highlighted on the slide once the composer drops
 * them. Left highlighted, pressing on them again starts a drag of the text
 * instead of a new selection, so the same words could not be picked twice.
 */
function releaseSlideHighlight() {
  const picked = typeof window === "undefined" ? null : window.getSelection();
  const anchor = picked?.anchorNode;
  const element = anchor instanceof Element ? anchor : anchor?.parentElement;
  if (picked && !picked.isCollapsed && element?.closest(".sd-slide")) picked.removeAllRanges();
}

/** The slide and highlight that apply to a message sent with this focus, or null. */
export function strategySlideContextFor(focus: AgentFocus, brandId: string | null | undefined) {
  const { current, cleared, selection, selectionSource, labels } = useStrategySlideContext.getState();
  if (!current || cleared || focus.surface !== "strategy" || focus.strategyId !== current.strategyId || brandId !== current.brandId) return null;
  return { slide: current, selection, selectionSource, labels };
}

const SOURCE_NOTE: Record<SlideTextSource, string> = {
  label: " (a fixed deck label, not editable)",
  value: " (from the strategy)",
  mixed: " (part fixed deck label, part strategy text)",
};

export function withStrategySlideContext(text: string, focus: AgentFocus, brandId: string): string {
  const context = strategySlideContextFor(focus, brandId);
  if (!context) return text;
  const { slide, selection, selectionSource, labels } = context;
  // Ordinary, visible message text: both transcript and server receive the same context.
  const lines = [`On slide: ${slide.label}${slide.pillarId ? ` (pillar ID: ${slide.pillarId})` : ""}`];
  // The deck's own copy is not in the strategy; Creo says so instead of editing (prompts/agent/modules/strategy.ts).
  if (labels.length) lines.push(`Fixed deck labels on this slide (not editable): ${labels.map(l => `"${l}"`).join(", ")}`);
  if (selection) lines.push(`Selected text on that slide${selectionSource ? SOURCE_NOTE[selectionSource] : ""}: "${selection}"`);
  // A highlight goes with one message, as an editor's chat does; the slide stays.
  if (selection) useStrategySlideContext.getState().setSelection(null);
  return `${lines.join("\n")}\n\n${text}`;
}
